import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceService } from '@core/services/device.service';
import { IHotelImage, IHotelTag } from '@core/models/hotel';
import { MbButton } from '@am/moebius';
import { NgIf, NgClass, NgFor } from '@angular/common';

@Component({
    selector: 'tags',
    templateUrl: './tags.component.html',
    styleUrls: ['./tags.component.scss'],
    standalone: true,
    imports: [
        NgIf,
        MbButton,
        NgClass,
        NgFor,
    ],
})
export class TagsComponent implements OnInit {
  public static MAX_TAGS = 52;
  @Input() items: IHotelImage[];
  @Output() updateTags: EventEmitter<IHotelImage[]> = new EventEmitter<IHotelImage[]>();

  public tags: IHotelTag[];
  public allItems: IHotelImage[];
  public activeAllTags: boolean;
  public isMobile: boolean;

  constructor(
    private analyticsService: AnalyticsService,
    private deviceService: DeviceService,
  ) {}

  ngOnInit() {
    const items = this.truncateItems([...this.items]);
    this.tags = this.createFilterTags(items);
    this.allItems = items;
    this.activeAllTags = false;
    this.isMobile = this.deviceService.isMobile();
  }

  public applyFilter(index: number, descriptionTag: string) {
    if (this.activeAllTags) {
      this.activeAllTags = false;
    }
    this.tags = this.updateFilterSelected(index, [...this.tags]);
    const tagsFiltrados = this.filterTags();
    this.updateTags.emit(tagsFiltrados);
    this.trackEvent(descriptionTag);
  }

  public applyAllTags() {
    this.activeAllTags = !this.activeAllTags;
    this.tags = this.resetTagsSelected();
    this.updateTags.emit([...this.allItems]);
    this.trackEvent('Todas las fotos');
  }

  /**
   * Create the filters by tags (name, total)
   * @param {IHotelImage[]} items
   * @param {IHotelTag[]} tags
   */
  private createFilterTags(items: IHotelImage[]) {
    const tagsWithNameAndTotal = {};
    items.forEach((item) => {
      if (tagsWithNameAndTotal[item.tag]) {
        tagsWithNameAndTotal[item.tag].total = tagsWithNameAndTotal[item.tag].total + 1;
      } else {
        const tagName = item.name;
        if (tagName) {
          tagsWithNameAndTotal[item.tag] = {
            name: tagName,
            total: 1,
          };
        }
      }
    });
    return Object.keys(tagsWithNameAndTotal).map((tag) => {
      return {
        id: tag,
        total: tagsWithNameAndTotal[tag].total,
        name: tagsWithNameAndTotal[tag].name,
        active: false,
      };
    });
  }

  /**
   * Update status for the tag selected by user, update array of tags
   * @param {number} index
   * @param {IHotelTag[]} tags
   * @return {IHotelTag[]}
   */
  private updateFilterSelected(index: number, tags: IHotelTag[]): IHotelTag[] {
    const itemSelected = { ...tags[index] };
    itemSelected.active = !itemSelected.active;
    tags[index] = itemSelected;
    return tags;
  }

  /**
   * Filter tags with status active
   * @return {IHotelImage[]}
   */
  private filterTags() {
    const idsTagFilter: string[] = this.tags
      .filter((tag) => tag.active)
      .map((tag) => tag.id);
    const tagsFilter: IHotelImage[] = this.allItems.filter((item) =>
      idsTagFilter.includes(item.tag),
    );
    return tagsFilter.length ? tagsFilter : this.allItems;
  }

  /**
   * Track event to analytics to notify tag filter action
   * @return void
   */
  private trackEvent(tagDescription: string) {
    this.analyticsService.trackEvent({
      section: 'Detail',
      action: 'Tag gallery',
      label: tagDescription,
    });
  }

  /**
   * Trucate length of images to show
   * @param {IHotelImage[]} items
   * @return {IHotelImage[]}
   */
  private truncateItems(items: IHotelImage[]) {
    if (items && items.length > TagsComponent.MAX_TAGS) {
      return items.slice(0, TagsComponent.MAX_TAGS);
    }
    return items;
  }

  /**
   * Create new array with active property in false to default status
   * @return {IHotelTag[]}
   */
  private resetTagsSelected() {
    return this.tags.map((tag) => {
      return {
        ...tag,
        active: false,
      };
    });
  }
}
